import { useState, useCallback } from "react";
import i18next from "i18next";

import {
  parseFileUploadError,
  formatErrorForLogging,
  type FileUploadError,
} from "@/utils/fileUploadErrorParser";
import { logger } from "@/telemetry";

export interface FileUploadErrorState {
  /** Parsed error information */
  error: FileUploadError | null;
  /** Whether an error is currently active */
  hasError: boolean;
  /** The file that failed to upload */
  failedFile: File | null;
}

export interface UseFileUploadErrorReturn {
  /** Current error state */
  errorState: FileUploadErrorState;
  /** Set an error for a file upload */
  setUploadError: (error: any, file: File, userId?: string, chatId?: string) => void;
  /** Clear the current error */
  clearError: () => void;
  /** Retry the failed upload */
  retryUpload: (() => void) | null;
}

/**
 * Custom hook for handling file upload errors
 *
 * Provides centralized error handling for file uploads with:
 * - Error parsing and formatting
 * - Logging with metadata
 * - Retry functionality
 * - Error state management
 *
 * @param onRetry - Optional callback function to retry the upload
 * @returns Error state and management functions
 *
 * @example
 * ```tsx
 * const { errorState, setUploadError, clearError, retryUpload } = useFileUploadError(handleRetry);
 *
 * try {
 *   await uploadFile(file);
 * } catch (error) {
 *   setUploadError(error, file, userId, chatId);
 * }
 * ```
 */
export function useFileUploadError(
  onRetry?: (file: File) => void | Promise<void>,
): UseFileUploadErrorReturn {
  const [errorState, setErrorState] = useState<FileUploadErrorState>({
    error: null,
    hasError: false,
    failedFile: null,
  });

  /**
   * Set an error for a file upload
   */
  const setUploadError = useCallback((error: any, file: File, userId?: string, chatId?: string) => {
    // Parse the error
    const parsedError = parseFileUploadError(error, file.name, file.size);

    // Log the error with full context
    const logData = formatErrorForLogging(error, file.name, file.size, userId, chatId);

    logger.warn(i18next.t("fileUpload.uploadFailed", { defaultValue: "Upload Failed" }), {
      logData: JSON.stringify(logData),
    });

    // Update state
    setErrorState({
      error: parsedError,
      hasError: true,
      failedFile: file,
    });
  }, []);

  /**
   * Clear the current error
   */
  const clearError = useCallback(() => {
    setErrorState({
      error: null,
      hasError: false,
      failedFile: null,
    });
  }, []);

  /**
   * Retry upload handler
   */
  const retryUpload = useCallback(() => {
    if (errorState.failedFile && onRetry) {
      // Clear error before retrying
      clearError();
      // Execute retry callback
      onRetry(errorState.failedFile);
    }
  }, [errorState.failedFile, onRetry, clearError]);

  return {
    errorState,
    setUploadError,
    clearError,
    retryUpload: errorState.failedFile && onRetry ? retryUpload : null,
  };
}
